import { isBrowser } from './is-browser';
import { getQueryParam } from './url/url';

import type { PageType } from '@shared/types/utils';

import PageTypes from '@shared/constants/page-types';

type Source = string | null | undefined | PageType;

const XSELL_MAP = 'XSellMap';
const HPA_REG = /^[A-Z]\w+Hpa$/;
const HOTEL_FEED_REG = /^[a-zA-Z]+Display$/;
const FLIGHT_PQS = 'FLIGHT_PQS';
const FLIGHT_DAYVIEW_ADS = 'FlightsDayviewAds';
const FLIGHT_DAIVIEW_OC = 'FlightsDayViewOC';
const HOME_ADS = 'HomeAds';
const HOME_ADS_APP = 'HomeAdsApp';
const NEW_RELIC = 'NewRelic';
const HOTELS_DEALS = 'hotels-deals';
const HOME_ADS_APP_NAV_CARD = 'HomeAdsAppNavCard';
const HOME_ADS_NAV_CARD = 'HomeAdsNavCard';
const COMBINED_EXPLORE_IDENTITY = 'CombinedExplore';
const SEO_TRAFFIC_SOURCE = 'SEO';
const HOTELS_LANDING_PAGE_SOURCE = 'seo';
const COMBINED_SEARCH = 'combined_search';
const PARALLEL_SEARCH = 'parallel_search';
const PARALLEL_SEARCH_FLIGHT_SEO = 'parallel_search_flight_seo';

const DEALS_PAGE_SOURCE = [
  HOME_ADS,
  HOME_ADS_APP,
  FLIGHT_DAYVIEW_ADS,
  HOME_ADS_APP_NAV_CARD,
  HOME_ADS_NAV_CARD,
];

const GREAT_PRICE_EXCLUDE_SOURCE = [FLIGHT_DAYVIEW_ADS, HOTELS_DEALS];

const FLIGHT_OTHER_SOURCE = [XSELL_MAP, FLIGHT_DAYVIEW_ADS, FLIGHT_DAIVIEW_OC];

const EXCLUDE_HIDE_CUG_SOURCE = [FLIGHT_PQS];

const RECOMMENDATIONS_XSELL_SOURCE = [COMBINED_SEARCH];

const PARALLEL_SEARCH_SOURCES = [PARALLEL_SEARCH, PARALLEL_SEARCH_FLIGHT_SEO];

const isFromFlightPQS = (source: Source) => source === FLIGHT_PQS;

const isFromFlightsDayviewAds = (source: Source) =>
  source === FLIGHT_DAYVIEW_ADS;

const isFromHPA = (source: Source) => !!(source && HPA_REG.test(source));

const isFromHotelFeed = (source: Source) =>
  !!(source && HOTEL_FEED_REG.test(source));

const isExcludeGreatPrice = (source: Source) =>
  !!(source && GREAT_PRICE_EXCLUDE_SOURCE.includes(source)) ||
  isFromHPA(source);

const isFromCombinedExplore = (source: Source) =>
  !!(source && source.includes(COMBINED_EXPLORE_IDENTITY));

const isExcludeHideCug = (source: Source) =>
  !!(source && EXCLUDE_HIDE_CUG_SOURCE.includes(source));

const isFromSeoTraffic = (source: Source) => source === SEO_TRAFFIC_SOURCE;

const isFromHotelsLandingPage = (source: Source) =>
  source === HOTELS_LANDING_PAGE_SOURCE;

const isFromRecommendationsXSell = (source: Source) =>
  !!source && RECOMMENDATIONS_XSELL_SOURCE.includes(source);

const isFromParallelSearch = (source: Source) =>
  !!source && PARALLEL_SEARCH_SOURCES.includes(source);

const trafficSource = ({ source }: { source: Source }): string => {
  if (
    isFromFlightPQS(source) ||
    isFromFlightsDayviewAds(source) ||
    isFromHPA(source) ||
    isFromHotelFeed(source) ||
    (source && FLIGHT_OTHER_SOURCE.includes(source)) ||
    (source && DEALS_PAGE_SOURCE.includes(source)) ||
    source === NEW_RELIC ||
    isFromCombinedExplore(source) ||
    isFromSeoTraffic(source) ||
    isFromRecommendationsXSell(source) ||
    isFromHotelsLandingPage(source) ||
    isFromParallelSearch(source)
  ) {
    return source as string;
  }

  if (source && Object.values(PageTypes).includes(source as PageType)) {
    return source
      .split('-')
      .reduce(
        (acc: string[], curr) => [
          ...acc,
          curr.charAt(0).toUpperCase(),
          curr.slice(1),
        ],
        [],
      )
      .join('');
  }

  return 'Unknown';
};

const getUtmSource = (utmSource: string) => {
  const regExp = /^([a-z-A-Z_]+)$/;
  if (utmSource && regExp.test(utmSource)) {
    return utmSource;
  }
  return null;
};

const getSource = () => {
  let source = isBrowser()
    ? getQueryParam(window.location.href, 'source')
    : null;
  source = trafficSource({
    source,
  });
  return source;
};

export {
  XSELL_MAP,
  FLIGHT_PQS,
  SEO_TRAFFIC_SOURCE,
  isFromFlightsDayviewAds,
  trafficSource,
  isFromHPA,
  isFromHotelFeed,
  isExcludeGreatPrice,
  isExcludeHideCug,
  getUtmSource,
  isFromSeoTraffic,
  isFromHotelsLandingPage,
  isFromRecommendationsXSell,
  getSource,
  isFromParallelSearch,
};
